import { setLocalAndForwardedRef } from '@squareup/dex-utils-dom';
import { ForwardedRef, useCallback, useRef, useState } from 'react';

const marketHydratedSet = new Set<string>();

const hasComponentHydrated = (name?: string | undefined) => {
  return Boolean(name && marketHydratedSet.has(name));
};

function useIsMarketComponentLoaded<T>(
  ref: ForwardedRef<T>,
  // Optional param to pass in if you want future components of this type to render immediately
  // We have to let the caller provide a name because we can only get the tag name once the component
  // is hydrated
  componentName?: string
): [boolean, (node: T) => void] {
  const [isMarketComponentLoaded, setIsMarketComponentLoaded] = useState(
    hasComponentHydrated(componentName)
  );

  const marketRef = useRef<T | null>(null);
  const onMarketRef = useCallback(
    (node: T) => {
      setLocalAndForwardedRef(marketRef, ref)(node);
      if (isMarketComponentLoaded) {
        return;
      }

      // Unfortunately, in an SSR world, we simply don't know when the
      // market component will be ready. So we'll use a 2 phased approach
      // 1. If not on the server, never show the market component. The reason for this
      //    is because the slotted content won't appear, and nextjs will complain about hydration errors.
      //    You'll also see flashes of content
      // 2. If on the client, don't show the market-component UNTIL it is hydrated. Web components are weird.
      //    They take time to hydrate, outside of the React render cycle. So we'll just poll for it until
      //    it's hydrated, and then finally show the tooltip
      function waitForWebComponentHydration() {
        if ((node as Element).hasAttribute('hydrated')) {
          requestAnimationFrame(() => {
            setIsMarketComponentLoaded(true);
            if (componentName) {
              marketHydratedSet.add(componentName);
            }
          });
        } else {
          setTimeout(() => {
            waitForWebComponentHydration();
          }, 10);
        }
      }

      if (node !== null) {
        waitForWebComponentHydration();
      }
    },
    [componentName, isMarketComponentLoaded, ref]
  );

  return [isMarketComponentLoaded, onMarketRef];
}

export { useIsMarketComponentLoaded };
